<template>
  <div id="custom-chat-root">
    <ChatWidget :config="config" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import ChatWidget from './components/ChatWidget.vue'
import type { N8nChatConfig } from '@/shared/types/n8n'

/**
 * Default chat configuration.
 * Used as fallback when config is not loaded or invalid.
 */
const DEFAULT_CONFIG: N8nChatConfig = {
  enabled: false,
  webhookUrl: '',
  mode: 'window',
  showWelcomeScreen: false,
  loadPreviousSession: true,
  colorPalette: 'default',
  customColors: {},
  dimensions: {
    width: '400px',
    height: '600px',
  },
  messages: {},
  initialMessages: [],
  allowFileUploads: false,
  allowedFilesMimeTypes: '',
  enableStreaming: false,
  chatInputKey: 'chatInput',
}

const config = ref<N8nChatConfig>({ ...DEFAULT_CONFIG })

/**
 * Validate that the response has the expected structure for N8nChatConfig.
 * This prevents runtime errors from malformed server responses.
 *
 * @param data - The data to validate
 * @returns True if data matches expected structure, false otherwise
 */
function isValidChatConfig(data: unknown): data is Partial<N8nChatConfig> {
  if (!data || typeof data !== 'object') {
    return false
  }

  const obj = data as Record<string, unknown>

  // Check required fields exist and have correct types
  if (typeof obj.enabled !== 'boolean') {
    return false
  }

  if (typeof obj.webhookUrl !== 'string') {
    return false
  }

  // Validate mode if present
  if (obj.mode !== undefined && obj.mode !== 'window' && obj.mode !== 'fullscreen') {
    return false
  }

  // Validate dimensions if present
  if (obj.dimensions !== undefined) {
    if (typeof obj.dimensions !== 'object' || obj.dimensions === null) {
      return false
    }
  }

  // Validate customColors if present
  if (obj.customColors !== undefined) {
    if (typeof obj.customColors !== 'object' || obj.customColors === null) {
      return false
    }
  }

  return true
}

/**
 * Merge validated config data with defaults to ensure all fields are present.
 *
 * @param data - Validated partial config from server
 * @returns Complete config with all required fields
 */
function mergeWithDefaults(data: Partial<N8nChatConfig>): N8nChatConfig {
  return {
    ...DEFAULT_CONFIG,
    ...data,
    dimensions: {
      ...DEFAULT_CONFIG.dimensions,
      ...(data.dimensions || {}),
    },
    customColors: {
      ...DEFAULT_CONFIG.customColors,
      ...(data.customColors || {}),
    },
    messages: {
      ...DEFAULT_CONFIG.messages,
      ...(data.messages || {}),
    },
  }
}

async function loadConfig() {
  try {
    const restUrl = (window as any).cnfltdChatConfig?.chatConfigUrl
      || `${window.location.origin}/wp-json/conflated-domains/v1/chat/config`

    const response = await fetch(restUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        console.info('[Custom Chat] Chat is not enabled or not configured')
        return
      }
      throw new Error(`Failed to fetch chat config: ${response.statusText}`)
    }

    const result = await response.json()

    // Validate response structure
    if (!result || typeof result !== 'object') {
      throw new Error('Invalid response format: expected object')
    }

    if (!result.success) {
      console.warn('[Custom Chat] Server returned unsuccessful response:', result.error || 'Unknown error')
      return
    }

    if (!result.data) {
      throw new Error('Invalid response format: missing data field')
    }

    // Validate config structure before using
    if (!isValidChatConfig(result.data)) {
      throw new Error('Invalid config structure: missing or invalid required fields')
    }

    // Merge with defaults to ensure all fields are present
    config.value = mergeWithDefaults(result.data)
  } catch (err) {
    console.error('[Custom Chat] Error loading config:', err)
  }
}

onMounted(() => {
  loadConfig()
})
</script>

<style scoped>
#custom-chat-root {
  /* Root container for custom chat */
}
</style>
